import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AutomationInterface, WorkflowStep } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { AutomationsFeatureCache } from '@galaxy/automata/shared';
import { FilterDefinitionService } from '../definitions/filter-definition.service';
import { WorkflowStepFilterComponent } from '../workflow-step-filter/workflow-step-filter.component';

@Component({
  selector: 'automata-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    GalaxyTooltipModule,
    WorkflowStepFilterComponent,
    GalaxyCheckboxModule,
  ],
})
export class FilterPanelComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() triggerFilters: WorkflowStep[];
  @Input() automation: AutomationInterface;
  @Input() selectedTaskDefinitionId: string;
  @ViewChildren(WorkflowStepFilterComponent) workflowStepFilterComponents: QueryList<WorkflowStepFilterComponent>;

  /** Emits an event once the component is created. */
  @Output() controlCreated: EventEmitter<any> = new EventEmitter();

  addConditionCheckboxForm: UntypedFormControl = new UntypedFormControl(false);
  private destroyed$$ = new Subject<void>();

  /** Emits true when loading the panel's contents, false otherwise.*/
  isLoading$ = new BehaviorSubject(false);

  private subscription: Subscription;

  showInternalTriggers$: Observable<boolean> = this.automationsFeatureFlags.includeInternalFeatures$;

  constructor(
    private readonly automationsFeatureFlags: AutomationsFeatureCache,
    private readonly filterDefinitions: FilterDefinitionService,
  ) {}

  ngOnInit(): void {
    this.addConditionCheckboxForm.setValue(this.triggerFilters?.length > 0);
    this.addConditionCheckboxForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => {
          if (!value) {
            this.triggerFilters = [];
            return;
          }

          if (value && this.triggerFilters.length === 0) {
            this.triggerFilters.push(
              new WorkflowStep({
                taskDefinitionId: '',
                choices: {
                  rules: [null],
                },
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.controlCreated.emit(this);
    this.subscribeToFilters();
    this.workflowStepFilterComponents.changes
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(() => this.subscribeToFilters());
  }

  private subscribeToFilters(): void {
    this.subscription?.unsubscribe();
    this.subscription = combineLatest(this.workflowStepFilterComponents.map((f) => f.isLoading()))
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((loadingStatus) => {
        // If all filters are done loading, we're done loading. Otherwise, loading continues.
        this.isLoading$.next(loadingStatus.includes(true));
      });
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    let result = [];
    this.workflowStepFilterComponents?.map((filterComponent) => {
      result = result.concat(...filterComponent.getControlsToValidateOnSave());
    });
    return result;
  }

  isFormValid(): boolean {
    let valid = true;
    this.workflowStepFilterComponents?.map((filterComponent) => {
      valid = valid && filterComponent.isFormValid();
    });
    return valid;
  }

  remove(index: number): void {
    this.triggerFilters.splice(index, 1);
  }

  removeAll(): void {
    this.triggerFilters = [];
    this.addConditionCheckboxForm.setValue(false);
  }

  addMore(): void {
    this.triggerFilters.push(
      new WorkflowStep({
        taskDefinitionId: this.triggerFilters[0].taskDefinitionId,
        choices: {
          rules: [null],
        },
      }),
    );
  }

  getWorkflowSteps(): WorkflowStep[] {
    const filterSteps = this.workflowStepFilterComponents?.map((filterComponent) => filterComponent.getWorkflowStep());
    filterSteps.map((step, index) => {
      if (index + 1 < filterSteps.length) {
        step.choices.rules[0].next = filterSteps[index + 1].id;
      }
      return step;
    });
    return filterSteps;
  }
}
